import {
  DOC_MASTER_ID,
  INVESTOR_TYPE_ID,
  INVESTOR_TYPE_ID_TO_NAME,
  INVESTOR_TYPE_NAME,
  KYC_VERIFICATION_MODE,
  MANUAL_KYC_MSG,
  MANUAL_KYC_REVIEW_DOC_LABELS,
  MANUAL_KYC_STEP_STATUS,
} from "../constants/constants";

const PLACEHOLDER = MANUAL_KYC_MSG.PLACEHOLDER;

const INDIVIDUAL_PAN_DOC_IDS = [
  DOC_MASTER_ID.PAN_CARD,
  DOC_MASTER_ID.PAN_VALUE,
  DOC_MASTER_ID.PAN_DOC,
];

const DIRECTOR_PAN_DOC_IDS = [
  DOC_MASTER_ID.DIRECTOR_PAN_DOC,
  DOC_MASTER_ID.DIRECTOR_PAN_VALUE,
];

const DIRECTOR_AADHAAR_DOC_IDS = [
  DOC_MASTER_ID.DIRECTOR_AADHAAR_DOC,
  DOC_MASTER_ID.DIRECTOR_AADHAAR_VALUE,
];

const STEP1_ID_DOC_IDS = [
  DOC_MASTER_ID.AADHAAR,
  DOC_MASTER_ID.DL,
  DOC_MASTER_ID.PASSPORT,
  DOC_MASTER_ID.VOTER_ID,
];

const SELFIE_DOC_IDS = [DOC_MASTER_ID.SELFIE, DOC_MASTER_ID.DIRECTOR_PHOTO];

const STEP2_GST_DOC_IDS = [
  DOC_MASTER_ID.GSTIN_DOC,
  DOC_MASTER_ID.GSTIN_VALUE,
  DOC_MASTER_ID.GST_CERT,
];

const ENTITY_PAN_DOC_IDS = [
  DOC_MASTER_ID.PAN_DOC,
  DOC_MASTER_ID.PAN_VALUE,
  DOC_MASTER_ID.COMPANY_PAN,
];

const BANK_DOC_IDS = [DOC_MASTER_ID.BANK_PROOF, DOC_MASTER_ID.BANK_STATEMENT];

export const isPdfFile = (url) => {
  if (!url) return false;
  return url.split("?")[0].toLowerCase().endsWith(".pdf");
};

export const getAsyncThunkErrorMessage = (payloadOrError, fallback) => {
  if (typeof payloadOrError === "string") return payloadOrError;
  return (
    payloadOrError?.error ||
    payloadOrError?.errorMsg ||
    payloadOrError?.message ||
    fallback
  );
};

export const resolveUserTypeId = ({ investorData = {}, details } = {}) =>
  investorData.userTypeId ||
  investorData.investorTypeId ||
  details?.business?.userTypeId ||
  details?.userDetails?.userTypeId ||
  "";

export const resolveUserTypeName = ({
  investorData = {},
  details,
  userTypeId,
} = {}) =>
  investorData.investorTypeName ||
  investorData.userTypeName ||
  details?.business?.userTypeName ||
  details?.userDetails?.userTypeName ||
  INVESTOR_TYPE_ID_TO_NAME[userTypeId] ||
  "";

export const isHufInvestor = (userTypeId, userTypeName) =>
  userTypeId === INVESTOR_TYPE_ID.HUF || userTypeName === INVESTOR_TYPE_NAME.HUF;

export const isEntityOrHufOrNbfcInvestor = (userTypeId, userTypeName) =>
  [
    INVESTOR_TYPE_ID.HUF,
    INVESTOR_TYPE_ID.ENTITY,
    INVESTOR_TYPE_ID.NBFC,
  ].includes(userTypeId) ||
  [
    INVESTOR_TYPE_NAME.HUF,
    INVESTOR_TYPE_NAME.ENTITY,
    INVESTOR_TYPE_NAME.NBFC,
  ].includes(userTypeName);

export const getDocFileUrl = (doc = {}) => doc.docUrl || doc.documentUrl;

export const filterDocsWithFiles = (documents = []) =>
  documents.filter((doc) => Boolean(getDocFileUrl(doc)));

export const filterManualKycStepDocs = (
  fileDocs = [],
  step,
  { isEntityOrHufOrNbfc } = {},
) => {
  if (step === 1) {
    return fileDocs.filter((doc) => {
      const id = doc.docMasterId;
      if (DIRECTOR_PAN_DOC_IDS.includes(id)) return true;
      if (DIRECTOR_AADHAAR_DOC_IDS.includes(id)) return true;
      if (INDIVIDUAL_PAN_DOC_IDS.includes(id) && !isEntityOrHufOrNbfc) return true;
      if (STEP1_ID_DOC_IDS.includes(id)) return true;
      if (SELFIE_DOC_IDS.includes(id)) return true;
      return false;
    });
  }

  if (step === 2) {
    return fileDocs.filter((doc) => {
      const id = doc.docMasterId;
      if (STEP2_GST_DOC_IDS.includes(id)) return true;
      if (ENTITY_PAN_DOC_IDS.includes(id) && isEntityOrHufOrNbfc) return true;
      if (id === DOC_MASTER_ID.PAN_CARD && isEntityOrHufOrNbfc) return true;
      return false;
    });
  }

  if (step === 3) {
    return fileDocs.filter((doc) => BANK_DOC_IDS.includes(doc.docMasterId));
  }

  return [];
};

export const getManualKycDocTitle = (
  doc = {},
  { isEntityOrHufOrNbfc, isHuf } = {},
) => {
  const id = doc.docMasterId;
  if (!isEntityOrHufOrNbfc && INDIVIDUAL_PAN_DOC_IDS.includes(id)) {
    return "PAN Card Document";
  }
  if (isEntityOrHufOrNbfc && ENTITY_PAN_DOC_IDS.includes(id)) {
    return isHuf ? "HUF PAN Document" : "Company / NBFC PAN Document";
  }
  return (
    MANUAL_KYC_REVIEW_DOC_LABELS[id] ||
    doc.docName ||
    doc.documentName ||
    "Submitted Document"
  );
};

export const getDocReviewStatusTag = (doc = {}) => {
  if (doc.isVerified) {
    return { label: "Verified", color: "success" };
  }
  if (doc.isRejected) {
    return { label: "Rejected", color: "error" };
  }
  return { label: "Pending Review", color: "warning" };
};

export const getStepStatusTagMeta = (status) => {
  if (status === MANUAL_KYC_STEP_STATUS.VERIFIED) {
    return { label: MANUAL_KYC_STEP_STATUS.VERIFIED, color: "success" };
  }
  if (status === MANUAL_KYC_STEP_STATUS.REJECTED) {
    return { label: MANUAL_KYC_STEP_STATUS.REJECTED, color: "error" };
  }
  if (status === MANUAL_KYC_STEP_STATUS.SUBMITTED) {
    return { label: MANUAL_KYC_STEP_STATUS.SUBMITTED, color: "processing" };
  }
  return { label: MANUAL_KYC_STEP_STATUS.PENDING, color: "warning" };
};

export const getStep1Title = ({ isHuf, isEntityOrHufOrNbfc }) => {
  if (isHuf) return "Step 1: Karta Identity & Address Verification";
  if (isEntityOrHufOrNbfc) return "Step 1: Director Identity & Address Verification";
  return "Step 1: Identity & Address Verification";
};

export const getStep2Title = ({ isHuf }) =>
  isHuf ? "Step 2: HUF Verification" : "Step 2: Business & Entity Verification";

export const getBankStepMeta = (isEntityOrHufOrNbfc, business = {}) => {
  const stepNum = isEntityOrHufOrNbfc ? 3 : 2;
  return {
    stepNum,
    status: isEntityOrHufOrNbfc ? business.step3Status : business.step2Status,
    title: isEntityOrHufOrNbfc
      ? "Step 3: Bank Account Verification"
      : "Step 2: Bank Account Verification",
    remarks: isEntityOrHufOrNbfc
      ? business.step3Remarks
      : business.step2Remarks,
  };
};

export const getIdentityPanLabel = ({ isHuf, isEntityOrHufOrNbfc }) => {
  if (isHuf) return "Karta PAN Number";
  if (isEntityOrHufOrNbfc) return "Director PAN Number";
  return "PAN Number";
};

export const getEntityPanLabel = ({ isHuf }) =>
  isHuf ? "HUF PAN Number" : "Entity / NBFC PAN Number";

export const resolveCompanyPanValue = (allDocs = [], isEntityOrHufOrNbfc) => {
  const companyPanDoc = allDocs.find(
    (doc) =>
      doc.docMasterId === DOC_MASTER_ID.PAN_DOC ||
      doc.docMasterId === DOC_MASTER_ID.PAN_VALUE ||
      (doc.docMasterId === DOC_MASTER_ID.PAN_CARD && isEntityOrHufOrNbfc),
  );
  return companyPanDoc?.docValue || companyPanDoc?.docNum || PLACEHOLDER;
};

export const resolveGstinValue = (business = {}, allDocs = []) => {
  const gstinDoc = allDocs.find(
    (doc) =>
      doc.docMasterId === DOC_MASTER_ID.GSTIN_VALUE ||
      doc.docMasterId === DOC_MASTER_ID.GSTIN_DOC,
  );
  return business.gstin || gstinDoc?.docValue || gstinDoc?.docNum || PLACEHOLDER;
};

export const displayOrPlaceholder = (value) => value || PLACEHOLDER;

export const formatCityState = (address = {}) =>
  `${displayOrPlaceholder(address.city || address.cityName)}, ${displayOrPlaceholder(address.state || address.stateName)}`;

export const formatStatePincode = (address = {}) =>
  `${displayOrPlaceholder(address.state || address.stateName)} - ${displayOrPlaceholder(address.postalCode || address.zipCode)}`;

export const formatBankNameBranch = (bank = {}) =>
  `${displayOrPlaceholder(bank.Ifsc?.BankMaster?.bankName || bank.bankName)} (${displayOrPlaceholder(bank.Ifsc?.branchName || bank.branchName)})`;

export const getRejectRemark = (...candidates) =>
  candidates.find(Boolean) || MANUAL_KYC_MSG.REJECT_DEFAULT_REMARK;

export const MANUAL_KYC_MODE_TAG = {
  label: KYC_VERIFICATION_MODE.MANUAL,
  color: "warning",
};
